<template>
  <div class="module">
    <Spinner v-if="getLoading"></Spinner>

    <transition name="slide-fade">
      <div class="charts panel" v-if="!getLoading">
        <div class="filters">
          <a
            class="filter"
            @click.prevent="filterUpdate(7)"
            href="#"
            :class="{ active: dayFilter === 7 && !customSearch }"
            >Week</a
          >

          <a
            class="filter"
            @click.prevent="filterUpdate(30)"
            href="#"
            :class="{ active: dayFilter === 30 && !customSearch }"
            >Month</a
          >

          <a
            class="filter"
            href="#"
            @click="toggleCustomDateFilter"
            :class="{ active: customSearch }"
            >Custom</a
          >
        </div>

        <!-- Custom date filter -->
        <form
          class="custom-filter"
          v-if="showCustomDateFilter"
          @submit.prevent="filterCustomDates(false)"
        >
          <label for="custom-start-date">
            <span>Start date</span>
            <input
              type="date"
              name="custom-start-date"
              id="custom-start-date"
              v-model="customStartDate"
              required
              :min="oneYearAgo"
              :max="todayFormatted"
            />
          </label>

          <label for="custom-end-date">
            <span>End date</span>
            <input
              type="date"
              name="custom-end-date"
              id="custom-end-date"
              v-model="customEndDate"
              required
              :min="oneYearAgo"
              :max="todayFormatted"
            />
          </label>

          <input class="button button--standard" type="submit" value="Go" />
        </form>

        <div class="date-change" v-if="!customSearch">
          <a @click.prevent="previousDates" class="previous-dates" href="#">
            <img
              :src="require('@/assets/img/arrow.svg')"
              alt="previous date set"
            />
          </a>

          <span
            >{{ formatDate(filterStartDate) }} -
            {{ formatDate(filterEndDate) }}</span
          >

          <a
            @click.prevent="nextDates"
            class="next-dates"
            href="#"
            v-if="!isFilterEndToday"
          >
            <img :src="require('@/assets/img/arrow.svg')" alt="next date set" />
          </a>
        </div>

        <component
          :is="currentModuleCharts"
          :existingEntries="existingEntries"
          v-if="!getLoading"
          :key="chartKey"
        ></component>
      </div>
    </transition>

    <transition name="slide-fade" v-if="!getLoading">
      <component :is="currentModuleGoals"></component>
    </transition>

    <transition name="slide-fade">
      <div class="entries" v-if="!getLoading">
        <details
          v-for="(day, index) in days"
          :key="index"
          :class="{ entry: day.entry }"
        >
          <summary :id="'accordion-' + index" @click="openAccordion">
            {{ formatDate(day.date) }}
            <span class="need-log" v-if="!day.entry"
              >Log your {{ module | unMachine }}</span
            >
            <Arrow class="arrow" :color="day.entry ? '#000' : '#fff'"></Arrow>
          </summary>

          <div class="details-content">
            <!-- Bring in the stats and operations for this module. -->
            <div v-if="!day.showForm">
              <div class="operations" v-if="!day.delete">
                <a
                  class="button button--standard"
                  @click.prevent="editEntry(day)"
                  href="#"
                  >edit</a
                >
                <a
                  class="button button--danger"
                  @click.prevent="toggleDelete(day)"
                  href="#"
                  >delete</a
                >
              </div>

              <div class="delete-confirm" v-if="day.delete">
                <p>
                  Are you sure you want to delete the entry for
                  {{ formatDate(day.date) }}?
                </p>
                <button
                  class="button button--standard"
                  @click="deleteEntry(day)"
                >
                  Confirm
                </button>
                <button
                  class="button button--danger"
                  @click="toggleDelete(day)"
                >
                  Cancel
                </button>
              </div>

              <div class="statistics-wrapper" v-if="!day.delete">
                <header class="stat-heading">
                  {{ module | capitalize }} statistics for
                  {{ formatDate(day.date) }}
                </header>
                <component
                  :is="currentModuleStats"
                  :existingEntry="day.entry"
                ></component>
              </div>
            </div>

            <!-- Load in the form for the module if there isn't an entry -->
            <component
              v-if="day.showForm"
              :is="currentModuleForm"
              @form-submit="formSubmit"
              @cancel-edit="cancelEdit"
              :existingEntry="day.entry"
              :day="day"
            ></component>
          </div>
        </details>
      </div>
    </transition>
  </div>
</template>

<script>
import { mapMutations, mapGetters } from "vuex";

import { subDays } from "date-fns";

import Spinner from "@/components/Spinner.vue";
import Arrow from "@/components/svg/Arrow.vue";

import firebase from "firebase/compat/app";
import "firebase/compat/firestore";
import "firebase/compat/auth";

import Utility from "@/mixins/utility";

export default {
  name: "Symptom",

  components: { Spinner, Arrow },

  mixins: [Utility],

  filters: {
    capitalize: function (value) {
      if (!value) return "";
      value = value.toString();
      return value.charAt(0).toUpperCase() + value.slice(1);
    },
  },

  async created() {
    // Update title immediately when component is created using route params directly
    const moduleName = this.$route.params.module;
    if (moduleName) {
      this.changePageTitle(moduleName);
      this.changePageIntroText("");
    }

    // Check for user.
    firebase.auth().onAuthStateChanged(async (user) => {
      if (user) {
        // Set the user id.
        this.setUID(user.uid);

        // Track module access for research analytics (Requirement IDs: 1, 3)
        const { trackModuleAccess } = await import("@/utils/analytics");
        trackModuleAccess("symptom", this.module, user.uid);

        // Grab pain entries.
        await this.firestoreGetPainEntries(
          this.filterStartDate,
          this.filterEndDate
        );

        // Set loading to true.
        this.modifyLoading(true);

        // Grabs entries and days.
        this.updateDays(
          7,
          this.filterStartDate,
          this.filterEndDate,
          this.module
        );

        // Set logged in to true.
        this.modifyLoggedIn(true);

        // Disable loader.
        this.disableLoader(1000);
      } else {
        // Send the user to the auth page if they aren't logged in.
        this.$router.push("/auth");
      }
    });
  },

  // Watch for route changes to update title
  watch: {
    $route(to, from) {
      // Update title when route parameter changes
      if (to.params.module !== from.params.module) {
        this.changePageTitle(to.params.module);
      }
    },
    // Also watch the computed module property
    module(newModule) {
      this.changePageTitle(newModule);
    },
  },

  computed: {
    ...mapGetters(["getLoading", "getAnalytics"]),

    // Imports the matching module form fields.
    currentModuleForm() {
      return () => import(`../components/symptom/${this.module}/Form.vue`);
    },

    // Imports the matching charts.
    currentModuleCharts() {
      return () => import(`../components/symptom/${this.module}/Charts.vue`);
    },

    // Imports the matching stats.
    currentModuleStats() {
      return () => import(`../components/symptom/${this.module}/Stats.vue`);
    },

    currentModuleGoals() {
      return () => import(`../components/symptom/${this.module}/Goals.vue`);
    },
  },
  methods: {
    ...mapMutations([
      "modifyLoggedIn",
      "changePageTitle",
      "changePageIntroText",
      "changeCurrentModule",
      "setDB",
      "setUID",
      "modifyLoading",
    ]),

    /**
     * Sets the day to edit mode.
     */
    editEntry(day) {
      day.showForm = true;
      day.edit = true;
    },

    /**
     * Toggles the delete boolean for the provided day.
     */
    toggleDelete(day) {
      day.delete = !day.delete;
    },

    /**
     * Deletes entry.
     *
     * @param object day
     *  Day object to delete entry from.
     */
    deleteEntry(day) {
      // Start the loader.
      this.modifyLoading(true);

      // Delete the entry from firebase.
      this.deleteFirestoreEntry(day.entry.id);

      // Remove the entry from our day object.
      delete day.entry;

      // Disable the loader.
      this.disableLoader(1000);
    },

    /**
     * Handles form submits.
     */
    formSubmit(data, day) {
      this.modifyLoading(true);

      // Create or update our log entry
      if (!day.edit) {
        this.createLogEntry(this.module, data, day.date);
      } else {
        this.updateLogEntry(day.entry.id, data);
      }

      // Increase the chart key to refresh the chart.
      this.chartKey += 1;

      // Turn off our loading animation.
      this.disableLoader(1000);
    },

    /**
     * Cancels form edit.
     */
    cancelEdit(entry) {
      entry.showForm = false;
      entry.edit = false;
    },

    /**
     * Creates a new entry in Firestore.
     *
     * @param string module
     * @param object data
     */
    createLogEntry(module, data, day) {
      this.getDB.collection("logs").add({
        user: this.getUID,
        module: module,
        date: day,
        data: data,
      });
    },

    /**
     * Updates an existing record and tracks the update event.
     */
    async updateLogEntry(id, data) {
      await this.getDB.collection("logs").doc(id).update({ data });

      // Track the update for analytics
      const { trackDataEntry } = await import("@/utils/analytics");
      trackDataEntry("symptom", this.module, this.getUID, "update");
    },

    /**
     * Deletes entry from Firestore.
     *
     * @param string id
     *  ID of the document you wish to delete.
     */
    deleteFirestoreEntry(id) {
      this.getDB.collection("logs").doc(id).delete();
    },
  },
};
</script>

<style>
.stat-stat {
  color: var(--michigan-blue);
  font-weight: 600;
}

.statistics {
  display: flex;
  flex-wrap: wrap;
  margin: -1rem;
}

.stat {
  flex-basis: 33%;
  margin-bottom: 1rem;
  padding: 1rem;
}

.operations {
  position: absolute;
  right: 1rem;
  top: 1rem;
}

.operations a:first-child {
  margin-right: 0.5rem;
}

.stat-heading {
  font-size: 1.125rem;
  font-weight: 600;
  margin-bottom: 2rem;
}

.delete-confirm p {
  margin-top: 0;
}

@media all and (max-width: 640px) {
  .operations {
    margin-bottom: 2rem;
    position: relative;
    top: 0;
    right: 0;
  }
  .statistics {
    display: block;
  }
}
</style>
